import { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  NotificationsRounded as BellIcon,
  DoneAllRounded as ReadIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAnnouncements, markAnnouncementsRead } from '@/services/api';
import { useI18n } from '@/contexts/I18nContext';
import type { Announcement } from '@/types';

interface AnnouncementBellProps {
  floating?: boolean;
}

export default function AnnouncementBell({ floating = false }: AnnouncementBellProps) {
  const queryClient = useQueryClient();
  const { formatDate, t } = useI18n();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: getAnnouncements,
    refetchInterval: 60_000,
  });

  const unreadCount = data?.unread_count ?? 0;
  const announcements = useMemo(() => data?.announcements ?? [], [data?.announcements]);
  const hasUnread = unreadCount > 0;

  const markReadMutation = useMutation({
    mutationFn: markAnnouncementsRead,
    onSuccess: () => {
      queryClient.setQueryData<{ announcements: Announcement[]; unread_count: number }>(
        ['announcements'],
        (current) =>
          current
            ? {
                announcements: current.announcements.map((announcement) => ({
                  ...announcement,
                  is_read: true,
                })),
                unread_count: 0,
              }
            : current
      );
    },
  });

  const handleOpen = () => {
    setOpen(true);
    if (hasUnread && !markReadMutation.isPending) {
      markReadMutation.mutate();
    }
  };

  return (
    <>
      <Box
        sx={{
          position: floating ? 'fixed' : 'relative',
          top: floating ? 'calc(var(--app-page-top-padding) + 38px)' : 'auto',
          right: floating
            ? 'max(calc(var(--app-page-inline-padding) + var(--app-safe-area-right)), calc((100vw - var(--app-content-max-width)) / 2 + var(--app-page-inline-padding)))'
            : 'auto',
          zIndex: floating ? 1100 : 1,
          flexShrink: 0,
        }}
      >
        <Badge
          showZero
          badgeContent={unreadCount}
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              minWidth: 20,
              height: 20,
              px: 0.6,
              borderRadius: 999,
              fontSize: '0.68rem',
              fontWeight: 900,
              color: '#FFFFFF',
              bgcolor: hasUnread ? '#FF3B30' : 'rgba(244, 255, 249, 0.20)',
              border: '2px solid #090D0E',
              boxShadow: hasUnread ? '0 4px 12px rgba(255, 59, 48, 0.38)' : 'none',
            },
          }}
        >
          <ButtonBase
            aria-label={t('announcements.unreadAria', { count: unreadCount })}
            onClick={handleOpen}
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              color: hasUnread ? '#06120D' : 'rgba(244, 255, 249, 0.58)',
              background: hasUnread
                ? 'linear-gradient(135deg, #76F4B8 0%, #FFD166 100%)'
                : 'rgba(244, 255, 249, 0.075)',
              border: hasUnread
                ? '1px solid rgba(255, 255, 255, 0.42)'
                : '1px solid rgba(226, 255, 238, 0.12)',
              boxShadow: hasUnread
                ? '0 12px 30px rgba(118, 244, 184, 0.26)'
                : '0 8px 24px rgba(0, 0, 0, 0.22)',
              backdropFilter: 'blur(18px) saturate(150%)',
              WebkitBackdropFilter: 'blur(18px) saturate(150%)',
              transition: 'transform 180ms cubic-bezier(0.2, 0, 0, 1), opacity 180ms ease',
              '&:active': {
                transform: 'scale(0.96)',
              },
            }}
          >
            {isLoading ? <CircularProgress size={18} color="inherit" /> : <BellIcon fontSize="small" />}
          </ButtonBase>
        </Badge>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h6">{t('announcements.title')}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <ReadIcon sx={{ fontSize: 17, color: hasUnread ? 'primary.main' : 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                {t('announcements.unread', { count: unreadCount })}
              </Typography>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {announcements.length === 0 ? (
            <Box
              sx={{
                py: 5,
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2">{t('announcements.empty')}</Typography>
            </Box>
          ) : (
            <Stack spacing={1.25} sx={{ pb: 1 }}>
              {announcements.map((announcement) => (
                <Box
                  key={announcement.id}
                  sx={(theme) => ({
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${
                      announcement.is_read
                        ? 'rgba(226, 255, 238, 0.10)'
                        : alpha(theme.palette.primary.main, 0.36)
                    }`,
                    bgcolor: announcement.is_read
                      ? 'rgba(244, 255, 249, 0.035)'
                      : alpha(theme.palette.primary.main, 0.08),
                  })}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2">{announcement.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        {announcement.content}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 800 }}>
                      {formatDate(announcement.created_at, { month: 'short', day: 'numeric' })}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
